import React, { useCallback, useEffect, useState } from "react";
import { Button, DatePicker, InputNumber, Segmented, Table, Tag, Typography } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Amount from "../shared/Amount";
import PageToolbar from "../../../../design-system/PageToolbar";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import { Error as ErrorMsg } from "../../../../commons/Messages/Messages";
import { errorText, trialBalance } from "./api";
import { TYPE_COLORS, TYPE_LABELS, csvMoney, downloadCsv, formatBalance } from "./labels";

/** Summen- und Saldenliste for a month, a year or a free date range. */
const TrialBalanceTab = () => {
  const { t } = useTranslation("accounting");
  const history = useHistory();
  const [mode, setMode] = useState("year");
  const [year, setYear] = useState(new Date().getFullYear());
  const [period, setPeriod] = useState(dayjs());
  const [range, setRange] = useState([dayjs().startOf("year"), dayjs()]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const params = useCallback(() => {
    if (mode === "period") return { period: period.format("YYYY-MM") };
    if (mode === "range")
      return { from: range[0].format("YYYY-MM-DD"), to: range[1].format("YYYY-MM-DD") };
    return { year };
  }, [mode, year, period, range]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await trialBalance(params());
      setRows(res.data.rows || []);
    } catch (err) {
      ErrorMsg(errorText(err, t("Could not build the trial balance")));
    } finally {
      setLoading(false);
    }
  }, [params, t]);

  useEffect(() => {
    load();
  }, [load]);

  const totalDebit = rows.reduce((s, r) => s + r.debit, 0);
  const totalCredit = rows.reduce((s, r) => s + r.credit, 0);
  const totalSoll = rows.filter(r => r.balance > 0).reduce((s, r) => s + r.balance, 0);
  const totalHaben = rows.filter(r => r.balance < 0).reduce((s, r) => s - r.balance, 0);

  const label = () => {
    const p = params();
    if (p.period) return p.period;
    if (p.year) return String(p.year);
    return `${p.from}_${p.to}`;
  };

  const exportCsv = () =>
    downloadCsv(
      `Summen-Saldenliste_${label()}.csv`,
      [
        t("Account"),
        t("Name"),
        t("Type"),
        t("Debit (Soll)"),
        t("Credit (Haben)"),
        t("Balance debit"),
        t("Balance credit")
      ],
      rows.map(r => [
        r.account,
        r.name,
        r.type,
        csvMoney(r.debit),
        csvMoney(r.credit),
        r.balance > 0 ? csvMoney(r.balance) : "",
        r.balance < 0 ? csvMoney(-r.balance) : ""
      ])
    );

  const columns = [
    {
      title: t("Account"),
      dataIndex: "account",
      width: 90,
      render: n => (
        <Typography.Link
          className="acct-num"
          onClick={() => history.push(`/user/accounting/journal/accounts/${n}`)}
        >
          {n}
        </Typography.Link>
      )
    },
    { title: t("Name"), dataIndex: "name", ellipsis: true },
    {
      title: t("Type"),
      dataIndex: "type",
      width: 130,
      render: v => (v ? <Tag color={TYPE_COLORS[v]}>{t(TYPE_LABELS[v] || v)}</Tag> : null)
    },
    { title: t("Entries"), dataIndex: "count", width: 80, align: "end" },
    {
      title: t("Debit (Soll)"),
      dataIndex: "debit",
      width: 130,
      align: "end",
      render: v => <Amount cents={v} />
    },
    {
      title: t("Credit (Haben)"),
      dataIndex: "credit",
      width: 130,
      align: "end",
      render: v => <Amount cents={v} />
    },
    {
      title: t("Balance debit"),
      key: "bs",
      width: 130,
      align: "end",
      render: (_, r) => (r.balance > 0 ? <Amount cents={r.balance} /> : "")
    },
    {
      title: t("Balance credit"),
      key: "bh",
      width: 130,
      align: "end",
      render: (_, r) => (r.balance < 0 ? <Amount cents={-r.balance} /> : "")
    }
  ];

  return (
    <div>
      <PageToolbar
        filters={
          <>
            <Segmented
              block
              value={mode}
              onChange={setMode}
              options={[
                { value: "period", label: t("Month") },
                { value: "year", label: t("Year") },
                { value: "range", label: t("Date range") }
              ]}
            />
            {mode === "period" ? (
              <DatePicker
                picker="month"
                value={period}
                onChange={v => v && setPeriod(v)}
                allowClear={false}
              />
            ) : null}
            {mode === "year" ? (
              <InputNumber value={year} onChange={v => v && setYear(v)} min={2000} max={2100} />
            ) : null}
            {mode === "range" ? (
              <DatePicker.RangePicker
                value={range}
                onChange={v => v && setRange(v)}
                format="DD.MM.YYYY"
                allowClear={false}
              />
            ) : null}
          </>
        }
        actions={
          <Button icon={<DownloadOutlined />} onClick={exportCsv} disabled={!rows.length}>
            {t("Export CSV")}
          </Button>
        }
      />
      <ResponsiveTable
        mobileLayout="table"
        className="acct-grid"
        rowKey="account"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 950 }}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={4}>
                <strong>{t("Totals")}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} align="end">
                <Amount cents={totalDebit} strong />
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="end">
                <Amount cents={totalCredit} strong />
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} align="end">
                <Amount cents={totalSoll} strong />
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7} align="end">
                <Amount cents={totalHaben} strong />
              </Table.Summary.Cell>
            </Table.Summary.Row>
            {totalDebit !== totalCredit ? (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={8}>
                  <Typography.Text type="danger">
                    {t("Debit and credit totals differ by {{amount}}", {
                      amount: formatBalance(totalDebit - totalCredit)
                    })}
                  </Typography.Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            ) : null}
          </Table.Summary>
        )}
      />
    </div>
  );
};

export default TrialBalanceTab;
